// Broker Commission Modeler — the broker's private what-if scenarios about
// their OWN compensation.
//
// Nothing here names a real Deal, Loan, DealBrokerFeeAuthorization, waterfall or
// payment, and that isolation is structural: there is no id of a real record
// anywhere in these shapes. The broker id is never on the wire either — the
// server resolves it from the portal session, so a request cannot name someone
// else's models.
//
// 🔴 EVERY FIGURE IS SERVER-COMPUTED. The result shapes below are read-only
// output; this client never derives one of them. `preview` exists precisely so
// responsiveness does not cost authority — an unsaved form still gets the
// server's arithmetic rather than a second, drifting copy of it. (The
// SenderLetterFigureComputer precedent: the client may show a figure, it may
// never be the source of one.)
//
// 🔴 PERCENTS ARE FRACTIONS ON THE WIRE. `monthlyFeeAmount` in
// percent_of_seller_share mode, `cashAtClosingPercent` in
// percent_of_down_payment mode, and `saleFeePercent` always, are FRACTIONS
// (0.05 = 5%) — identical to DealBrokerFeeAuthorization. The UI works in whole
// percents and converts at the boundary; see commissionModeler/copy.ts.

import { portalApi } from "./client";

/** A `LoanBrokerFeeMode` value. Same three strings as the real Deal card's
 *  Broker Fee Authorization — one vocabulary, so a broker never learns two
 *  names for the same arrangement. */
export type BrokerFeeMonthlyMode = "none" | "percent_of_seller_share" | "flat_monthly";

/** A `BrokerCashAtClosingMode` value — the ONE-TIME cash arrangement.
 *
 *  🔴 Deliberately NOT `BrokerFeeMonthlyMode`, and the two must never be unified
 *  — the same rule the C# enum's own doc comment carries. They sit inches apart
 *  on every shape below and are structurally identical, so a shared type or a
 *  shared label map will look obviously correct. What differs is what they gate:
 *  the monthly mode sits in the chain that decides whether a broker is payable at
 *  all, while cash at closing gates nothing (it is paid directly seller → broker
 *  and never touches Phoenix's rails). Their percentages are also taken on
 *  different bases — the seller's monthly share up there, the buyer's DOWN
 *  PAYMENT here — so one vocabulary serving both would leave a broker unable to
 *  tell which basis they picked. */
export type BrokerCashAtClosingMode = "none" | "flat" | "percent_of_down_payment";

/** Wire form of `CommissionScheduleGranularity`. The API serialises every enum
 *  with JsonStringEnumConverter (Program.cs), so these arrive as their C# member
 *  NAMES, not ints. Above a 24-month horizon the server buckets by year. */
export type CommissionScheduleGranularity = "Monthly" | "Yearly";

/** The program terms a model is computed against — Phoenix-set, displayed
 *  read-only, never broker-supplied. Rates are FRACTIONS (0.10 = 10%). */
export interface CommissionModelTerms {
  interestRate: number;
  /** Schedule B's rate. The buyer pays the full contract rate; Phoenix retains
   *  the difference as its servicing spread and the seller receives principal
   *  plus the rest. THIS, not the buyer's payment, is what "% of seller's
   *  monthly share" is a percentage of. */
  sellerInterestRate: number;
  amortizationMonths: number;
  /** The modeled horizon: ongoing commission accrues months 1..this. The name
   *  is historical — the month-12 note sale it named is retired from the seller
   *  program, and no surface calls it a liquidity event any more. */
  liquidityEventMonth: number;
}

/** One row of the modeled schedule. Bucketing is the result's `granularity`;
 *  every money member is a SUM over the bucket except `noteOutstandingPrincipal`
 *  and `cumulativeBrokerCommission`, which are end-of-bucket values.
 *
 *  🔴 This row carries TWO amortizations of the same note and they do not agree.
 *  The seller trio (`sellerPrincipal` + `sellerInterest` = `sellerReceipt`,
 *  exactly, every row) describes how the seller's receipt is COMPOSED at the
 *  seller's rate. `noteOutstandingPrincipal` is the balance of the note itself on
 *  the buyer's contract terms — the only real balance, and the basis the sale fee
 *  is computed on. The note retires principal more slowly than the seller-rate
 *  description credits it, so deal size minus the summed `sellerPrincipal` column
 *  does NOT equal `noteOutstandingPrincipal`. Do not "reconcile" them: the
 *  mitigation is the two-tier header and the "on the note" label.
 *
 *  `buyerPayment` / `principal` / `interest` are the buyer-side split. Retained
 *  because the note balance derives from them; NOT rendered anywhere. */
export interface CommissionScheduleRow {
  period: number;
  buyerPayment: number;
  principal: number;
  interest: number;
  noteOutstandingPrincipal: number;
  sellerReceipt: number;
  sellerPrincipal: number;
  sellerInterest: number;
  brokerCommission: number;
  cumulativeBrokerCommission: number;
  /** The last month modeled. No surface marks it. */
  isLiquidityEvent: boolean;
}

/** The full workup, computed server-side in decimal. */
export interface CommissionModelResult {
  note: number;
  /** Rendered on the BROKER'S OWN SCREEN and nowhere else — never on the
   *  generated report, which is a client-facing document. See `phoenixSpread`. */
  buyerMonthlyPayment: number;
  /** What the seller receives each month: principal plus interest at the
   *  seller's rate. The base for any ongoing commission percentage. */
  sellerReceipt: number;
  /** buyerMonthlyPayment − sellerReceipt.
   *
   *  🔴 SCREEN ONLY. The workup's reconciliation block shows seller share +
   *  spread = buyer payment because that identity is the entire reason the
   *  modeler exists: a broker's fee is a percentage of the SELLER'S share, and
   *  mistaking it for the buyer's payment overstates the commission by about
   *  1.46x at the standard terms. A broker seeing all three add up is how they
   *  learn which one they are paid on.
   *
   *  On the REPORT neither figure appears at all. That document goes to the
   *  seller, and Phoenix's retained servicing spread is not a number to put in
   *  front of them — there it survives as fine-print prose without a figure,
   *  pinned by PdfRenderSmokeTests asserting the amounts and the words "Buyer
   *  payment" are absent from the rendered bytes. */
  phoenixSpread: number;
  brokerMonthlyCommission: number;
  /** 🔴 The RESOLVED dollar, whatever the arrangement: 0 for `none`, the flat
   *  amount for `flat`, down payment × percent for `percent_of_down_payment`.
   *  The member kept its name when the mode arrived, which is what left
   *  `lifetimeTotal` and every surface reading it correct and untouched. Never
   *  re-derive it here — the inputs on `CommissionModelDto` describe the
   *  arrangement, this is the server's arithmetic on it. */
  brokerCashAtClosing: number;
  ongoingTotal: number;
  principalAmortized: number;
  /** Computed server-side but rendered nowhere and counted in no total: the
   *  end-of-horizon note sale the sale fee was charged on is retired from the
   *  seller program (PR #1322). */
  remainingPrincipalAtLiquidity: number;
  saleFeeAmount: number;
  /** cash at closing + ongoing. The ONE combined headline. */
  lifetimeTotal: number;
  horizonMonths: number;
  granularity: CommissionScheduleGranularity;
  schedule: CommissionScheduleRow[];
  terms: CommissionModelTerms;
}

/** Create/update payload. `monthlyFeeAmount` is a FRACTION in percent mode and
 *  dollars per month in flat mode; `saleFeePercent` is a FRACTION or null. */
export interface SaveCommissionModelRequest {
  name: string;
  clientName: string | null;
  totalDealSize: number;
  /** In `percent_of_down_payment` mode this is ALSO the basis the cash
   *  percentage resolves against — the modeler needs no separate basis field,
   *  unlike the deal card, because a down payment is already a required input. */
  downPayment: number;
  /** The FLAT dollar amount, read by the server only in `flat` mode. */
  brokerCashAtClosing: number;
  monthlyFeeMode: BrokerFeeMonthlyMode;
  monthlyFeeAmount: number;
  saleFeePercent: number | null;
  cashAtClosingMode: BrokerCashAtClosingMode;
  /** A FRACTION of `downPayment` (0.10 = 10%), read only in
   *  `percent_of_down_payment` mode; null otherwise. */
  cashAtClosingPercent: number | null;
}

/** A saved model plus its freshly computed workup.
 *
 *  The cash-at-closing members here are the stored INPUTS, echoed back so a
 *  surface can re-render the form and describe the arrangement in words. The
 *  resolved dollar is `result.brokerCashAtClosing` and nothing may re-derive it
 *  from these. */
export interface CommissionModelDto {
  id: string;
  name: string;
  clientName: string | null;
  totalDealSize: number;
  downPayment: number;
  brokerCashAtClosing: number;
  monthlyFeeMode: BrokerFeeMonthlyMode;
  monthlyFeeAmount: number;
  saleFeePercent: number | null;
  createdAt: string;
  modifiedAt: string | null;
  result: CommissionModelResult;
  cashAtClosingMode: BrokerCashAtClosingMode;
  cashAtClosingPercent: number | null;
}

/** A row in the broker's saved-model list. Carries the lifetime total so the
 *  list is useful without recomputing a full schedule per row. */
export interface CommissionModelSummaryDto {
  id: string;
  name: string;
  clientName: string | null;
  totalDealSize: number;
  lifetimeTotal: number;
  createdAt: string;
  modifiedAt: string | null;
}

export const commissionModelerApi = {
  /** The authenticated broker's saved models. */
  list: () => portalApi.get<CommissionModelSummaryDto[]>("/commission-modeler"),
  /** One saved model plus its freshly computed workup (404 when not theirs). */
  get: (id: string) => portalApi.get<CommissionModelDto>(`/commission-modeler/${id}`),
  create: (body: SaveCommissionModelRequest) =>
    portalApi.post<CommissionModelDto>("/commission-modeler", body),
  update: (id: string, body: SaveCommissionModelRequest) =>
    portalApi.put<CommissionModelDto>(`/commission-modeler/${id}`, body),
  remove: (id: string) => portalApi.delete<void>(`/commission-modeler/${id}`),
  /** Compute a workup WITHOUT saving. The live preview's only source — this is
   *  what keeps a responsive form from becoming a second implementation of the
   *  arithmetic. */
  preview: (body: SaveCommissionModelRequest) =>
    portalApi.post<CommissionModelResult>("/commission-modeler/preview", body),
  /** The client-facing Commission Arrangement Report as a PDF blob.
   *
   *  Per MODEL, not per period — this is a document about one scenario, so there
   *  is nothing to pick a month for and the shared StatementDownloadBar (a month
   *  picker) is the wrong shape. 404s for a scenario that isn't the session
   *  broker's, exactly like `get`. */
  report: (id: string) => portalApi.getBlob(`/commission-modeler/${id}/report`),
};
